import * as React from "react";

import { AlertDialog, PageLayout } from "@/shared/components";
import {
  DatasetsList as SharedDatasetsList,
  type DatasetRow,
} from "@/shared/components/datasets/DatasetsList";
import {
  type PendingDatasetAction,
  useDatasetBulkActions,
} from "@/shared/components/datasets/useDatasetBulkActions";
import { useNotification } from "@/shared/components/notifications/useNotification";
import { type SelectionOption } from "@/shared/components/ResourceView";
import { useDeleteDatasets } from "@/shared/domain/datasets/hooks";
import { DeleteOutlineIcon } from "@/shared/foundation/icons/mui";

import { CreateDatasetPopoverButton } from "./CreateDatasetPopoverButton";

export function DatasetsList() {
  const bulkActions = useDatasetBulkActions();
  const { mutateAsync: deleteDatasets } = useDeleteDatasets();
  const showNotification = useNotification();

  const [pendingDelete, setPendingDelete] =
    React.useState<PendingDatasetAction | null>(null);

  const selectionOptions: SelectionOption<DatasetRow>[] = React.useMemo(
    () => [
      ...bulkActions.selectionOptions,
      {
        name: "Delete",
        icon: <DeleteOutlineIcon fontSize="small" />,
        action: ({ selected, clearSelection }) => {
          setPendingDelete({
            ids: selected.map((d) => d.dataset_id),
            clearSelection,
          });
        },
      },
    ],
    [bulkActions.selectionOptions],
  );

  const handleDeleteConfirm = async () => {
    if (pendingDelete === null) {
      return;
    }
    const results = await deleteDatasets(pendingDelete.ids);
    const failures = results.filter((r) => r.status === "rejected").length;
    if (failures > 0) {
      showNotification(
        `${failures} dataset${failures !== 1 ? "s" : ""} could not be deleted`,
        "error",
      );
    }
    pendingDelete.clearSelection();
  };

  return (
    <PageLayout title="Roboto - Datasets" headerContentLeft="Datasets">
      <SharedDatasetsList
        selectionOptions={selectionOptions}
        actions={<CreateDatasetPopoverButton />}
        noData={{
          description: "Create one to upload and organize your data.",
          action: (
            <CreateDatasetPopoverButton
              variant="secondary"
              popoverAlign="center"
            />
          ),
        }}
      />
      {bulkActions.addToCollectionDialog}
      <AlertDialog
        dialogOpen={pendingDelete !== null}
        handleClose={() => setPendingDelete(null)}
        dialogTitle={`Delete ${pendingDelete?.ids.length ?? 0} dataset${pendingDelete?.ids.length !== 1 ? "s" : ""}?`}
        dialogText="This action cannot be undone."
        dialogAction={handleDeleteConfirm}
      />
    </PageLayout>
  );
}
